import React, { useState } from "react";
import Icon from "components/AppIcon";

const LooptFoundationResults = ({ data, formatCurrency, formatNumber }) => {
  const [expandedSections, setExpandedSections] = useState({
    inputs: true,
    calculations: true,
    results: true,
  });

  if (!data) return null;

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Helper function to format costs as positive values since they represent expenses
  const formatCost = (value) => {
    return formatCurrency(Math.abs(value));
  };

  const inputValues = [
    { label: "Material Type", value: data.inputs.material, icon: "Package" },
    {
      label: "Units",
      value: formatNumber(data.inputs.looptUnits),
      icon: "Hash",
    },
    {
      label: "Material Weight",
      value: `${formatNumber(data.inputs.looptMaterialWeight)} LBS`,
      icon: "Weight",
    },
    {
      label: "COGS per Unit",
      value: formatCurrency(data.inputs.looptCogsPerUnit),
      icon: "DollarSign",
    },
    {
      label: "Fee per Pound",
      value: formatCurrency(data.looptFoundation.feePerPound),
      icon: "Scale",
    },
    {
      label: "Corporate Tax Rate",
      value: `${formatNumber(data.inputs.looptCorporateTaxRate)}%`,
      icon: "Receipt",
    },
  ];

  const calculationSteps = [
    {
      label: "Processing Fee",
      formula: "Material Weight × Fee per Pound",
      calculation: `${formatNumber(
        data.inputs.looptMaterialWeight
      )} LBS × ${formatCost(data.looptFoundation.feePerPound)}`,
      result: Math.abs(data.looptFoundation.processingFee),
      type: "currency",
      isNegative: true,
      valueColor: "text-error",
    },
    {
      label: "COGS Gross",
      formula: "Units × COGS per Unit",
      calculation: `${formatNumber(data.inputs.looptUnits)} × ${formatCurrency(
        data.inputs.looptCogsPerUnit
      )}`,
      result: data.looptFoundation.cogsGross,
      type: "currency",
    },
    {
      label: "FMV (Fair Market Value)",
      formula: "Same as COGS Gross",
      calculation: `${formatCurrency(data.looptFoundation.cogsGross)}`,
      result: data.looptFoundation.fmv,
      type: "currency",
    },
    {
      label: "EBITA Positive Impact",
      formula: "FMV × Corporate Tax Rate",
      calculation: `${formatCurrency(
        data.looptFoundation.fmv
      )} × ${formatNumber(data.inputs.looptCorporateTaxRate)}%`,
      result: data.looptFoundation.ebitaPositiveImpact,
      type: "currency",
    },
    {
      label: "Transportation Estimate",
      formula: "Fixed Transportation Cost",
      calculation: "Predetermined estimate",
      result: Math.abs(data.looptFoundation.transportationEstimate),
      type: "currency",
      isNegative: true,
      valueColor: "text-error",
    },
    {
      label: "Net EBITA Contribution",
      formula: "EBITA Impact - Processing Fee - Transportation",
      calculation: `${formatCurrency(
        data.looptFoundation.ebitaPositiveImpact
      )} - ${formatCost(data.looptFoundation.processingFee)} - ${formatCost(
        data.looptFoundation.transportationEstimate
      )}`,
      result: data.looptFoundation.netEbitaContribution,
      type: "currency",
      highlight: true,
      valueColor: "text-text-primary",
    },
  ];

  const finalResults = [
    {
      label: "Net EBITA Contribution",
      value: data.looptFoundation.netEbitaContribution,
      type: "currency",
      icon: "Calculator",
      description: "Final net contribution after all calculations",
      color: "text-text-primary",
      bgColor: "bg-success-50",
      borderColor: "border-success-200",
      iconColor: "text-success",
    },
    {
      label: "EBITA Impact per Unit",
      value: data.looptFoundation.netEbitaContribution / data.inputs.looptUnits,
      type: "currency",
      icon: "Package",
      description: "Net EBITA contribution per unit",
    },
    {
      label: "Tax Benefit Ratio",
      value:
        (data.looptFoundation.ebitaPositiveImpact / data.looptFoundation.fmv) *
        100,
      type: "percentage",
      icon: "Receipt",
      description: "Tax benefit as percentage of FMV",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <div className="w-10 h-10 bg-success-100 rounded-lg flex items-center justify-center">
          <Icon name="Recycle" size={20} className="text-success" />
        </div>
        <div>
          <h3 className="text-xl font-heading-semibold text-text-primary">
            Donation Model Results
          </h3>
          <p className="text-sm text-text-secondary">
            Sustainable textile processing with environmental benefits
          </p>
        </div>
      </div>

      {/* Input Values Section */}
      <div className="bg-surface-50 rounded-lg border border-border-light">
        <button
          onClick={() => toggleSection("inputs")}
          className="w-full px-4 py-3 flex items-center justify-between text-left focus-ring"
        >
          <div className="flex items-center space-x-3">
            <Icon name="FileText" size={16} className="text-text-secondary" />
            <span className="font-body-medium text-text-primary">
              Input Values
            </span>
          </div>
          <Icon
            name="ChevronDown"
            size={16}
            className={`text-text-tertiary transition-transform duration-300 ${
              expandedSections.inputs ? "rotate-180" : ""
            }`}
          />
        </button>

        {expandedSections.inputs && (
          <div className="px-4 pb-4 border-t border-border-light">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              {inputValues.map((input, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-3 p-3 bg-surface rounded-lg border border-border"
                >
                  <Icon
                    name={input.icon}
                    size={16}
                    className="text-text-secondary"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-body-medium text-text-primary">
                      {input.label}
                    </div>
                    <div className="text-sm text-text-secondary">
                      {input.value}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Calculation Steps Section */}
      <div className="bg-surface-50 rounded-lg border border-border-light">
        <button
          onClick={() => toggleSection("calculations")}
          className="w-full px-4 py-3 flex items-center justify-between text-left focus-ring"
        >
          <div className="flex items-center space-x-3">
            <Icon name="Calculator" size={16} className="text-text-secondary" />
            <span className="font-body-medium text-text-primary">
              Calculation Steps
            </span>
          </div>
          <Icon
            name="ChevronDown"
            size={16}
            className={`text-text-tertiary transition-transform duration-300 ${
              expandedSections.calculations ? "rotate-180" : ""
            }`}
          />
        </button>

        {expandedSections.calculations && (
          <div className="px-4 pb-4 border-t border-border-light">
            <div className="space-y-3 mt-3">
              {calculationSteps.map((step, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg border ${
                    step.highlight
                      ? "bg-success-50 border-success-200"
                      : "bg-surface border-border"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div
                        className={`font-body-medium ${
                          step.color ||
                          (step.highlight
                            ? "text-success"
                            : "text-text-primary")
                        }`}
                      >
                        {step.label}
                      </div>
                      <div className="text-sm text-text-secondary mt-1">
                        {step.formula}
                      </div>
                      <div className="text-xs text-text-tertiary mt-1 font-data">
                        {step.calculation}
                      </div>
                    </div>
                    <div
                      className={`text-right ${
                        step.valueColor ||
                        step.color ||
                        (step.highlight ? "text-success" : "text-text-primary")
                      }`}
                    >
                      <div
                        className={`font-data ${
                          step.highlight ? "text-lg" : "text-base"
                        }`}
                      >
                        {step.type === "currency"
                          ? step.isNegative
                            ? `-${formatCurrency(step.result)}`
                            : formatCurrency(step.result)
                          : formatNumber(step.result)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Final Results Section */}
      <div className="bg-surface-50 rounded-lg border border-border-light">
        <button
          onClick={() => toggleSection("results")}
          className="w-full px-4 py-3 flex items-center justify-between text-left focus-ring"
        >
          <div className="flex items-center space-x-3">
            <Icon name="BarChart3" size={16} className="text-text-secondary" />
            <span className="font-body-medium text-text-primary">
              Final Results
            </span>
          </div>
          <Icon
            name="ChevronDown"
            size={16}
            className={`text-text-tertiary transition-transform duration-300 ${
              expandedSections.results ? "rotate-180" : ""
            }`}
          />
        </button>

        {expandedSections.results && (
          <div className="px-4 pb-4 border-t border-border-light">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-3">
              {finalResults.map((result, index) => (
                <div
                  key={index}
                  className={`rounded-lg border p-4 ${
                    result.bgColor && result.borderColor
                      ? `${result.bgColor} ${result.borderColor}`
                      : "bg-surface border-border"
                  }`}
                >
                  <div className="flex items-center space-x-3 mb-2">
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        result.iconColor === "text-success"
                          ? "bg-success-100"
                          : "bg-success-100"
                      }`}
                    >
                      <Icon
                        name={result.icon}
                        size={16}
                        className={result.iconColor || "text-success"}
                      />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-body-medium text-text-primary">
                        {result.label}
                      </div>
                    </div>
                  </div>
                  <div
                    className={`text-2xl font-data mb-1 ${
                      result.color || "text-success"
                    }`}
                  >
                    {result.type === "currency"
                      ? formatCurrency(result.value)
                      : result.type === "percentage"
                      ? `${formatNumber(result.value)}%`
                      : formatNumber(result.value)}
                  </div>
                  <div className="text-xs text-text-tertiary">
                    {result.description}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LooptFoundationResults;
